import { jsx, jsxs } from 'react/jsx-runtime';
import { CheckIcon, ArrowUpIcon } from '@radix-ui/react-icons';
import React__default from 'react';
import { styled } from 'styled-components';
import { AnonBottomBarSignup } from '../ExpandedResource/ExtraPanel/AnonBottomBarSignup.js';
import { ExpandedFdocElements } from './ExpandedFdocElements.js';
import { AnalyticsEvents } from '../../../analytics/analytics.types.js';
import { useAnalytics } from '../../../analytics/hooks/useAnalytics.js';
import { useMutationCreateComment } from '../../../comments/mutations/useMutationCreateComment.js';
import { useMutationUpdateComment } from '../../../comments/mutations/useMutationUpdateComment.js';
import { useQueryUserInfo } from '../../../user/queries/useQueryUserInfo.js';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { framerAnimationSlideUp } from '@fabric-app/ui/constants/framerAnimations';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import { ForcedTheme } from '@fabric-app/ui/theme/ForcedTheme';
import { useResourceDataContextSafe } from '../context/resourceDataContext.js';
import { CloseIcon } from '@fabric-app/icons/icons';

const ExpandedFdocSidebarAddComment = ({ resourceId, editingComment, onCancelEditingComment, canAutoFocus = true }) => {
  const { track } = useAnalytics();
  const { disabledActions } = useResourceDataContextSafe();
  const { data: user } = useQueryUserInfo();
  const [textAreaRef, setTextAreaRef] = React__default.useState(null);
  const [content, setContent] = React__default.useState("");
  const wasEditingComment = React__default.useRef(Boolean(editingComment));
  React__default.useEffect(() => {
    if (editingComment) {
      setContent(editingComment.content);
      setTimeout(() => {
        textAreaRef?.focus();
      }, 200);
    } else if (wasEditingComment.current) {
      setContent("");
      textAreaRef?.blur();
    }
    wasEditingComment.current = Boolean(editingComment);
  }, [
    editingComment,
    textAreaRef
  ]);
  const handleTextAreaChange = (e) => {
    setContent(e.target.value);
  };
  const mutationCreateComment = useMutationCreateComment();
  const mutationUpdateComment = useMutationUpdateComment();
  const handleSubmit = () => {
    const trimmed = content.trim();
    if (trimmed && user) {
      setContent("");
      if (editingComment) {
        mutationUpdateComment.mutate({
          comment: editingComment,
          newContent: trimmed
        }, {
          onSuccess: () => {
            onCancelEditingComment();
          },
          onError: () => {
            setContent(trimmed);
          }
        });
      } else {
        mutationCreateComment.mutate({
          resourceId,
          content: trimmed
        }, {
          onSuccess: () => {
            track(AnalyticsEvents.CreatedComment, {
              action: "expanded_fdoc"
            });
          },
          onError: () => {
            setContent(trimmed);
          }
        });
      }
    }
  };
  const handleTextAreaKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSubmit();
      e.preventDefault();
    }
  };
  useRunOnce(() => {
    if (!canAutoFocus) return;
    window.requestAnimationFrame(() => {
      textAreaRef?.focus();
    });
  }, {
    condition: Boolean(textAreaRef)
  });
  if (disabledActions.addOrEditOwnComment) {
    return /* @__PURE__ */ jsx(AnonBottomBarSignup, {
      children: /* @__PURE__ */ jsx("p", {
        children: disabledActions.addOrEditOwnComment
      })
    });
  }
  return /* @__PURE__ */ jsx(ForcedTheme, {
    colorScheme: "light",
    asChild: true,
    children: /* @__PURE__ */ jsxs(Conversation.Form, {
      ...framerAnimationSlideUp,
      children: [
        editingComment && /* @__PURE__ */ jsxs(ExpandedFdocElements.ItemPreviewSidebarCommentsEditBox, {
          onClick: onCancelEditingComment,
          children: [
            /* @__PURE__ */ jsx(EditCommentCloseWrapper, {
              children: /* @__PURE__ */ jsx(CloseIcon, {})
            }),
            /* @__PURE__ */ jsx("span", {
              children: "Editing comment"
            })
          ]
        }),
        /* @__PURE__ */ jsxs(Conversation.FormField, {
          children: [
            /* @__PURE__ */ jsx(Conversation.FormTextArea, {
              placeholder: "Add a comment",
              value: content,
              onChange: handleTextAreaChange,
              onKeyDown: handleTextAreaKeyDown,
              ref: setTextAreaRef,
              autoFocus: canAutoFocus,
              rows: 1,
              "data-testid": "expanded-fdoc-comment-input"
            }),
            /* @__PURE__ */ jsx(Conversation.FormButton, {
              "aria-label": "Add a comment",
              title: "Add a comment",
              disabled: !content || content.trim().length === 0,
              onClick: handleSubmit,
              "data-testid": "expanded-fdoc-comment-submit",
              children: editingComment ? /* @__PURE__ */ jsx(CheckIcon, {
                style: {
                  strokeWidth: 2
                }
              }) : /* @__PURE__ */ jsx(ArrowUpIcon, {})
            })
          ]
        })
      ]
    })
  });
};
const EditCommentCloseWrapper = styled.div.withConfig({
  displayName: "ExpandedFdocSidebar_AddComment__EditCommentCloseWrapper",
  componentId: "sc-5ac55739-0"
})([
  `border-radius:50%;height:1.0625rem;width:1.0625rem;background:white;display:flex;align-items:center;justify-content:center;color:black;svg{height:0.6125rem;width:0.6125rem;}`
]);

export { ExpandedFdocSidebarAddComment };
//# sourceMappingURL=ExpandedFdocSidebar_AddComment.js.map
